import { useState } from "react";
import type { Answer, DeliveryRow, Direction } from "../../api";
import { formatAmount, formatShortTimestamp } from "../../format";
import { ago, answerFigures, answerKey, creditorOf, dayKey, dayLabel, hhmm, isProblem, KINDS, plural, receiptState, type Case } from "./model";
import { Mark } from "./Status";

// The board's body: two columns on one grid, what we sent ↔ what Nets sent
// back. A matched pair is one line with a connector between its halves; a
// missing receipt is an empty right half; a possible double is one left, two
// right; an ambiguous group is one box whose lefts and rights are joined by a
// single "?", never by lines; receipts of no delivery have an empty left.
// Problems first, each with what it is and what to do; then everything else by
// upload day, quiet. Every row — heads, problems, accepted — has the same box,
// so the columns line up down the whole page.

export type Open = (direction: Direction, name: string) => void;

export function CaseList({ cases, now, onOpen }: { cases: Case[]; now: number; onOpen: Open }): JSX.Element {
  const problems = cases.filter(isProblem);
  const days = new Map<string, Case[]>();
  for (const c of cases.filter((c) => !isProblem(c))) days.set(dayKey(c.at), [...(days.get(dayKey(c.at)) ?? []), c]);
  return (
    <div className="cases">
      <Heads />
      {problems.length > 0 ? (
        <section>
          <h3 className="board-section">Needs attention</h3>
          {problems.map((c) => (
            <CaseRow key={c.key} c={c} now={now} onOpen={onOpen} showDay />
          ))}
        </section>
      ) : null}
      {[...days.entries()]
        .sort(([a], [b]) => b.localeCompare(a))
        .map(([day, list]) => (
          <section key={day}>
            <h3 className="board-section">
              {list[0] ? dayLabel(list[0].at) : day} · {plural(list.reduce((n, c) => n + c.deliveries.length, 0), "delivery", "deliveries")}
            </h3>
            {list.map((c) => (
              <CaseRow key={c.key} c={c} now={now} onOpen={onOpen} showDay={false} />
            ))}
          </section>
        ))}
    </div>
  );
}

/** The column heads, on the rows' own grid and box. */
function Heads(): JSX.Element {
  return (
    <div className="case case-heads" aria-hidden="true">
      <div className="case-grid">
        <span />
        <span className="case-side">
          <b>We sent</b>
          <span className="case-sent">
            <span>Uploaded</span>
            <span>Creditor</span>
            <span className="right">Collections</span>
            <span className="right">Total</span>
          </span>
        </span>
        <span />
        <span className="case-side">
          <b>Nets sent back</b>
          <span className="case-recv">
            <span>Stamped</span>
            <span>Nets no.</span>
            <span>Receipts</span>
            <span>Their count</span>
          </span>
        </span>
      </div>
    </div>
  );
}

function Sent({ row, showDay }: { row: DeliveryRow; showDay: boolean }): JSX.Element {
  const creditor = creditorOf(row);
  return (
    <span className="case-sent">
      <span className="case-time">
        {showDay ? <span className="case-day">{dayLabel(row.uploadedAt)}</span> : null} {hhmm(row.uploadedAt)}
      </span>
      <span title={creditor.full}>{creditor.short}</span>
      <span className="right">{row.deliveryType === "0605" ? plural(row.items, "stop") : row.items}</span>
      <span className="right">{row.totalOere === null ? "" : formatAmount(row.totalOere)}</span>
    </span>
  );
}

/** What Nets counted, said only beside a problem: an accepted pair's counts matched, or it would not be accepted. */
function counted(answer: Answer, against: DeliveryRow | undefined, problem: boolean): JSX.Element | null {
  if (answer.records === null || answer.files.some((f) => f.kind === "gateway_error")) return null;
  if (!problem) return null;
  if (answer.amountLines === null) return <span className="dim">{answerFigures(answer)}</span>;
  if (against === undefined) return <span>{answerFigures(answer)}</span>;
  const same = answer.amountLines === against.items && answer.totalOere === against.totalOere;
  return same ? <span className="dim">= same as ours</span> : <span className="case-differs">{answerFigures(answer)}</span>;
}

function Received({ answer, against, showDay, problem }: { answer: Answer; against: DeliveryRow | undefined; showDay: boolean; problem: boolean }): JSX.Element {
  const state = receiptState(answer);
  return (
    <span className="case-recv">
      <span className="case-time">
        {showDay ? <span className="case-day">{dayLabel(answer.at)}</span> : null} {hhmm(answer.at)}
      </span>
      <span>{answer.deliveryNumber ?? ""}</span>
      <span>
        <Mark tone={state.tone} mark={state.mark} word={state.word} />
      </span>
      <span className="case-count">{counted(answer, against, problem)}</span>
    </span>
  );
}

/** Whether a set of receipts is paired with one of the case's deliveries, rather than only fitting it. */
const placed = (c: Case, answer: Answer): boolean =>
  c.deliveries.some((row) => [row.answer, ...row.extra].some((a) => a !== null && answerKey(a) === answerKey(answer)));

/** How the two sides relate: one to one, one to several, any to any, or nothing on one side. */
function linkOf(c: Case): "one" | "fork" | "any" | "none" | "wait" {
  if (c.kind === "waiting") return "wait";
  if (c.deliveries.length === 0 || c.answers.length === 0) return "none";
  if (c.deliveries.length > 1 || c.answers.every((a) => !placed(c, a))) return "any";
  return c.answers.length > 1 ? "fork" : "one";
}

const LINK_MARK = { one: "⟶", fork: "⟶", any: "?", none: "✕", wait: "…" } as const;

function CaseRow({ c, now, onOpen, showDay }: { c: Case; now: number; onOpen: Open; showDay: boolean }): JSX.Element {
  const [open, setOpen] = useState(false);
  const info = KINDS[c.kind];
  const rows = Math.max(c.deliveries.length, c.answers.length, 1);
  const problem = isProblem(c);
  const only = c.deliveries.length === 1 ? c.deliveries[0] : undefined;
  // A group whose deliveries are alike is compared against any of them.
  const alike = c.deliveries.every((row) => row.items === c.deliveries[0]?.items && row.totalOere === c.deliveries[0]?.totalOere) ? c.deliveries[0] : undefined;
  const link = linkOf(c);
  const span = { gridRow: `1 / span ${rows}` };
  return (
    <div className="case" data-tone={info.tone} data-problem={problem || undefined}>
      <div className="case-grid">
        <span className="case-status" style={span}>
          {c.kind === "accepted" ? null : <Mark tone={info.tone} mark={info.mark} word={c.kind === "ambiguous_group" ? "Ambiguous" : info.label} />}
          <button type="button" className="case-files-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? "▾" : "▸"} files
          </button>
        </span>
        {c.deliveries.length > 0 ? (
          c.deliveries.map((row, i) => (
            <span key={row.name} className="case-side" style={{ gridRow: i + 1 }}>
              <Sent row={row} showDay={showDay} />
            </span>
          ))
        ) : (
          <span className="case-side case-void" style={span}>
            nothing of ours fits
          </span>
        )}
        <span className="case-link" data-link={link} style={span}>
          {LINK_MARK[link]}
          {link === "fork" ? <small>×{c.answers.length}</small> : null}
        </span>
        {c.answers.length > 0 ? (
          c.answers.map((a, i) => (
            <span key={answerKey(a)} className="case-side case-right" style={{ gridRow: i + 1 }}>
              <Received answer={a} against={alike} showDay={showDay} problem={problem} />
            </span>
          ))
        ) : (
          <span className="case-side case-right case-void" style={span}>
            {c.kind === "waiting" ? "not yet" : only ? `nothing, ${ago(only.uploadedAt, now).replace(" ago", "")} after upload` : "nothing"}
          </span>
        )}
      </div>
      {problem ? (
        <p className="case-why">
          {c.sentence}
          {info.action ? <b> {info.action}</b> : null}
        </p>
      ) : null}
      {open ? <CaseFiles deliveries={c.deliveries} answers={c.answers} onOpen={onOpen} /> : null}
    </div>
  );
}

const FILE_KIND: Record<string, string> = { transmission: "T", validation: "V", gateway_error: "Gateway notice" };

/** Every file behind a case, each opening in the file view: the details a glance does not need. */
function CaseFiles({ deliveries, answers, onOpen }: { deliveries: DeliveryRow[]; answers: Answer[]; onOpen: Open }): JSX.Element {
  return (
    <dl className="case-files">
      {deliveries.map((row) => (
        <div key={row.name}>
          <dt>
            Sent {dayLabel(row.uploadedAt)} {hhmm(row.uploadedAt)}
          </dt>
          <dd>
            <button type="button" className="linkish mono" onClick={() => onOpen("outbound", row.name)}>
              {row.name}
            </button>
            <span className="dim">
              {" "}
              {creditorOf(row).full} · {plural(row.records, "record")}
              {row.migrated ? (row.uploadTimeKnown ? " · migrated copy" : " · migrated copy, upload time unknown") : ""}
              {row.matchedBy === "counts" ? " · paired by the bank's counts" : row.matchedBy === "name" ? " · paired by name" : ""}
            </span>
            {row.status !== "accepted" ? <span className="case-files-note">{row.reason}</span> : null}
          </dd>
        </div>
      ))}
      {answers.map((answer) => (
        <div key={answerKey(answer)}>
          <dt>Nets {answer.deliveryNumber ?? "—"}</dt>
          <dd>
            {answer.files.map((f) => (
              <span key={f.name}>
                <button type="button" className="linkish mono" onClick={() => onOpen("inbound", f.name)}>
                  {f.name}
                </button>
                <span className="dim">
                  {" "}
                  {FILE_KIND[f.kind] ?? f.kind} {f.positive ? "✓" : "✗"} {[f.code, f.text].filter(Boolean).join(" ")}
                  {f.at ? ` · ${formatShortTimestamp(f.at)}` : ""}
                </span>
              </span>
            ))}
            {answer.conflict ? <span className="case-files-note">{answer.conflict}</span> : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}
